import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatINR } from '@new-wealth/shared';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { useConfirm } from '../components/Confirm.js';

interface SeriesRow {
  series_id: number; code: string; name: string; status: string;
  pending_count: number; pending_amount: string;
  total_count: number; total_amount: string;
  /** Distinct PEOPLE holding an Active investment — not the same as total_count,
   *  since one customer often holds several in a series. */
  customer_count: number;
  /** Active investments with a signature on file, by either method (owner
   *  2026-09-04). Counted over the same Active set as total_count, so the pair
   *  reads as a fraction of the same thing. */
  signed_count: number;
  esigned_count: number;
  physically_signed_count: number;
  /** The date the series was allotted, from the date stamped on its investments.
   *  Null while it is still Open. */
  allotment_date: string | null;
  pending_request_id: number | null;   // an allotment approval awaiting a checker
}

// A 0-pending series is still actionable if it can be formally closed (moved to
// Allotted) — i.e. it isn't already Allotted/Withdrawn.
const isCloseOnly = (s: SeriesRow) => s.pending_count === 0 && s.status !== 'Allotted' && s.status !== 'Withdrawn';

export function AllotmentsPage() {
  const { confirm, promptText } = useConfirm();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [msg, setMsg] = useState('');
  const { data, isLoading, error } = useQuery({ queryKey: ['allot-series'], queryFn: () => api.get<{ rows: SeriesRow[] }>('/api/allotments/series') });
  const allot = useMutation({
    mutationFn: (seriesId: number) => api.post(`/api/allotments/series/${seriesId}`, { allotment_date: date }),
    onSuccess: () => { setMsg('Batch submitted — a second checker must approve it on the Approvals page.'); qc.invalidateQueries({ queryKey: ['allot-series'] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Failed'),
  });
  const revert = useMutation({
    // Reason is collected by the caller: a prompt inside mutationFn cannot be
    // awaited, so a cancelled/suppressed dialog used to revert anyway with the
    // fallback reason.
    mutationFn: ({ seriesId, reason }: { seriesId: number; reason: string }) => api.post(`/api/allotments/series/${seriesId}/revert`, { reason }),
    onSuccess: () => { setMsg('Allotment reverted.'); qc.invalidateQueries({ queryKey: ['allot-series'] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Failed'),
  });
  const cancelPending = useMutation({
    mutationFn: (seriesId: number) => api.post(`/api/allotments/series/${seriesId}/cancel-pending`, {}),
    onSuccess: () => { setMsg('Pending allotment cancelled — you can allot again.'); qc.invalidateQueries({ queryKey: ['allot-series'] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Failed'),
  });
  if (isLoading) return <div className="text-text-muted">Loading…</div>;
  if (error) return <div className="text-danger">Failed to load series.</div>;

  const columns: Column<SeriesRow>[] = [
    // The series name is the way into its customers (owner 2026-08-28) — the
    // list of who holds it, who referred them, and their bonds.
    { key: 'code', header: 'Series', tdClassName: 'font-semibold',
      value: (s) => s.code,
      render: (s) => <Link to={`/app/allotments/${s.series_id}`} className="text-primary hover:underline">{s.code}</Link> },
    { key: 'name', header: 'Name' },
    // Customers before Investments: the smaller, human number reads first, and
    // the pair together shows how concentrated a series is.
    { key: 'customer_count', header: 'Customers', align: 'right', value: (s) => s.customer_count },
    { key: 'total_count', header: 'Investments', align: 'right', value: (s) => s.total_count },
    // Signed paperwork, which no screen or report counted before (owner
    // 2026-09-04: "how to know how many e signs have been made in a series").
    // Sorted on the SHARE, not the count: a series with 5 of 129 is in worse
    // shape than one with 2 of 2, and ranking by the raw number hides that.
    { key: 'signed_count', header: 'Signed', align: 'right',
      value: (s) => (s.total_count ? s.signed_count / s.total_count : -1),
      render: (s) => {
        if (!s.total_count) return <span className="text-text-muted">—</span>;
        const all = s.signed_count >= s.total_count;
        const none = s.signed_count === 0;
        const bits = [
          s.esigned_count ? `${s.esigned_count} e-signed` : '',
          s.physically_signed_count ? `${s.physically_signed_count} on paper` : '',
        ].filter(Boolean).join(' · ');
        return (
          <span className="inline-flex flex-col items-end leading-tight"
            title={bits || 'Nothing signed in this series yet'}>
            <span className={`mono ${all ? 'text-success' : none ? 'text-danger' : 'text-warn'}`}>
              {s.signed_count} / {s.total_count}
            </span>
            {/* The split only means something once there IS a split. */}
            {bits && <span className="text-[11px] text-text-muted">{bits}</span>}
          </span>
        );
      } },
    { key: 'total_amount', header: 'Investment amount', align: 'right',
      value: (s) => Number(s.total_amount),
      render: (s) => (
        <span className="inline-flex flex-col items-end">
          <span className="mono">{formatINR(s.total_amount)}</span>
          {s.pending_count > 0 && <span className="text-xs text-primary">{s.pending_count} to allot</span>}
        </span>
      ) },
    { key: 'status', header: 'Status' },
    // Next to Status, because it is the detail Status implies but never showed
    // (owner 2026-08-28). Blank while a series is still Open — there is no date
    // yet, and a dash says that more honestly than an empty cell.
    { key: 'allotment_date', header: 'Allotted on', align: 'right',
      value: (s) => s.allotment_date ?? '',
      render: (s) => s.allotment_date
        ? <span className="mono">{String(s.allotment_date).slice(0, 10)}</span>
        : <span className="text-text-muted">—</span> },
    { key: 'actions', header: 'Actions', sortable: false, filterable: false, align: 'right', tdClassName: 'whitespace-nowrap',
      render: (s) => {
        const revertBtn = (
          <button onClick={async () => { setMsg(''); if (await confirm({ title: `Cancel the pending allotment request for ${s.code}?`, body: 'Nothing is allotted; you can submit it again.', confirmLabel: 'Cancel request', danger: true })) cancelPending.mutate(s.series_id); }}
            className="text-xs border border-border text-danger rounded px-3 py-1.5 hover:bg-[color:var(--danger-bg)]">↺ Revert</button>
        );
        // 1) Awaiting a checker → Pending approval (disabled) + Revert (cancel it).
        if (s.pending_request_id) {
          return (
            <span className="inline-flex gap-2 justify-end">
              <button disabled className="text-xs border border-border text-text-muted rounded px-3 py-1.5 cursor-default">Pending approval</button>
              {revertBtn}
            </span>
          );
        }
        // 2) Allotted → only Revert (un-stamp; super-admin/allotments:revert).
        if (s.status === 'Allotted') {
          return can('allotments:revert')
            ? <button onClick={async () => {
                setMsg('');
                const reason = await promptText({ title: `Revert the allotment for ${s.code}?`, body: 'The investments in it go back to un-allotted.', label: 'Reason', confirmLabel: 'Revert', danger: true });
                if (reason) revert.mutate({ seriesId: s.series_id, reason });
              }} className="text-xs border border-border text-danger rounded px-3 py-1.5 hover:bg-[color:var(--danger-bg)]">↺ Revert</button>
            : <span className="text-xs text-text-muted">—</span>;
        }
        // 3) Otherwise → Allot / Close (re-enabled once a pending request is cancelled/rejected).
        const closeOnly = isCloseOnly(s);
        const canAllot = s.pending_count > 0 || closeOnly;
        return canAllot
          ? <button disabled={allot.isPending} onClick={() => { setMsg(''); allot.mutate(s.series_id); }}
              title={closeOnly ? 'No pending investments — this closes the series to new money' : undefined}
              className="text-xs bg-primary text-white rounded px-3 py-1.5 disabled:opacity-40 hover:bg-primary-hover">{closeOnly ? 'Close series' : 'Allot batch'}</button>
          : <span className="text-xs text-text-muted">—</span>;
      } },
  ];

  return (
    <div className="w-full">
      <h1 className="text-xl font-bold tracking-tight m-0">Allotments</h1>
      <p className="text-sm text-text-muted mt-1 mb-4">Every series and its total investment amount. Allot a series once its investments are active — this stamps the allotment date and closes the series to new money. It does not change the book. Submitting starts a maker-checker approval.</p>
      <div className="flex items-center gap-2 mb-4">
        <label className="text-sm text-text-label">Allotment date</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-2.5 py-1.5 text-sm border border-border-strong rounded" />
      </div>
      {msg && <div className="text-xs text-primary mb-3">{msg}</div>}
      <DataTable
        columns={columns}
        rows={data!.rows}
        rowKey={(s) => s.series_id}
        defaultSort={{ key: 'code', dir: 'desc' }}
        empty="No series configured."
      />
    </div>
  );
}
